import { Socket } from 'phoenix';
import axios from 'axios';
import { store } from '../store';

// ============================================================================
// КОНФИГУРАЦИЯ СЕРВИСОВ
// ============================================================================

const getServiceUrls = () => {
  if (typeof window !== 'undefined' && window.ENV) {
    return window.ENV;
  }
  return {
    universe_gateway: 'http://localhost:4000',
    imcon: 'http://localhost:4001',
    world_manager: 'http://localhost:4002',
    sim_character: 'http://localhost:4003',
    character_designer: 'http://localhost:4004',
  };
};

const SERVICE_URLS = getServiceUrls();

// ============================================================================
// КОНСТАНТЫ И ВАЛИДАЦИЯ
// ============================================================================

const CHARACTER_TYPES = {
  ACTIVE: 'active',
  DESIGN: 'design',
  ALL: 'all'
};

const SCHEME_TYPES = {
  SHIP: 'ship',
  STRUCTURE: 'structure',
  VEHICLE: 'vehicle',
  MODULE: 'module',
  STATION: 'station',
  SPACEPORT: 'spaceport',
  SHIPYARD: 'shipyard',
  CIVIL_STRUCTURE: 'civil_structure',
  MILITARY_STRUCTURE: 'military_structure'
};

const ALLOWED_CHARACTER_TYPES = [
  'warrior', 'scholar', 'priest', 'ruler', 'artisan', 'explorer', 'diplomat',
  'scientist', 'engineer', 'pilot', 'medic', 'entrepreneur', 'hunter',
  'farmer', 'creator', 'mystic', 'harmonist', 'captain', 'scout',
  'technician', 'miner', 'analyst', 'security', 'trader', 'programmer',
  'builder', 'versatile', 'healer'
];

const isAuthenticated = () => {
  if (typeof window === 'undefined') return false;
  try {
    const state = store.getState();
    return state.auth?.isAuthenticated === true;
  } catch (error) {
    console.warn('Error checking authentication:', error);
    return false;
  }
};

const validateCharacterType = (characterType) => {
  if (!characterType) return false;
  try {
    const type = characterType.toLowerCase();
    return ALLOWED_CHARACTER_TYPES.includes(type);
  } catch (error) {
    console.warn('Error validating character type:', error);
    return false;
  }
};

// ============================================================================
// HTTP КЛИЕНТЫ
// ============================================================================

const createHttpClient = (baseURL, serviceName) => {
  const client = axios.create({
    baseURL: baseURL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json'
    },
    withCredentials: true,
  });

  client.interceptors.request.use(
    (config) => {
      try {
        let token = null;
        let tokenType = null;

        if (typeof localStorage !== 'undefined') {
          const kratosToken = localStorage.getItem('kratos_session_token');
          if (kratosToken) {
            token = kratosToken;
            tokenType = 'kratos_session';
          }
        }

        if (!token) {
          try {
            const state = store.getState();
            const reduxToken = state.auth?.accessToken || state.auth?.token;
            if (reduxToken) {
              token = reduxToken;
              tokenType = 'redux_token';
            }
          } catch (error) {
            console.warn('Could not get state from store:', error);
          }
        }

        if (token && typeof token === 'string' && token.length > 0) {
          const isJWT = token.split('.').length === 3;

          if (isJWT && tokenType === 'kratos_session') {
            config.headers.Authorization = `Bearer ${token}`;
            console.log(`🔐 [${serviceName}] Using JWT as Bearer for ${config.url}`);
          } else if (isJWT) {
            console.log(`⚠️ [${serviceName}] JWT token found but not using for HTTP (WebSocket only)`);
          } else {
            config.headers['X-Session-Token'] = token;
            console.log(`🔑 [${serviceName}] Using X-Session-Token for ${config.url}`, {
              tokenPrefix: token.substring(0, 20) + '...',
              tokenLength: token.length
            });
          }
        } else {
          console.log(`🍪 [${serviceName}] No token found, using cookies for ${config.url}`);
        }

        if (serviceName === 'UNIVERSE' && config.url?.includes('/api/player/')) {
          console.log(`📤 [${serviceName}] Player endpoint request:`, {
            url: config.url,
            method: config.method,
            hasAuthorization: !!config.headers.Authorization,
            hasXSessionToken: !!config.headers['X-Session-Token'],
            tokenType: tokenType
          });
        }

      } catch (error) {
        console.warn(`⚠️ [${serviceName}] Auth interceptor error:`, error);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  client.interceptors.response.use(
    (response) => {
      console.log(`✅ [${serviceName}] Response ${response.status} ${response.config.url}`);
      return response;
    },
    (error) => {
      console.error(`❌ [${serviceName}] Response error:`, {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
      });

      const isAuthEndpoint = error.config?.url?.includes('/api/game/auth') ||
        error.config?.url?.includes('/auth/') ||
        error.config?.url?.includes('/self-service/');

      const isGameEndpoint = error.config?.url?.includes('/api/player/') ||
        error.config?.url?.includes('/api/game/');

      if ((error.response?.status === 401 || error.response?.status === 403) &&
        !isAuthEndpoint && !isGameEndpoint) {
        handleAuthError();
      }
      return Promise.reject(error);
    }
  );

  return client;
};

const handleAuthError = () => {
  if (typeof window === 'undefined') return;
  console.warn('🛑 Authentication error detected');

  try {
    store.dispatch({ type: 'auth/logout' });
  } catch (error) {
    console.warn('Could not dispatch logout action:', error);
  }

  console.warn('ℹ️ Session expired. LoginModal will open automatically.');
};

const httpClients = {
  universe: createHttpClient(SERVICE_URLS.universe_gateway, 'UNIVERSE'),
  imcon: createHttpClient(SERVICE_URLS.imcon, 'IMCON'),
  world: createHttpClient(SERVICE_URLS.world_manager, 'WORLD'),
  simCharacter: createHttpClient(SERVICE_URLS.sim_character, 'SIM_CHARACTER'),
  designer: createHttpClient(SERVICE_URLS.character_designer, 'DESIGNER'),
};

// ============================================================================
// УТИЛИТЫ
// ============================================================================

const buildWebSocketUrl = (httpUrl, socketPath = '/socket') => {
  try {
    const url = new URL(httpUrl);
    url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    const basePath = url.pathname.replace(/\/+$/, '');
    url.pathname = `${basePath}${socketPath}`;
    url.search = '';
    return url.toString();
  } catch (error) {
    const wsProtocol = httpUrl.startsWith('https') ? 'wss' : 'ws';
    const cleaned = httpUrl.replace(/^https?:\/\//, `${wsProtocol}://`).replace(/\/+$/, '');
    return `${cleaned}${socketPath}`;
  }
};

const normalizeCharacterPlacement = (character) => {
  if (!character) return null;

  return {
    ...character,
    spawn_object_id: character.spawn_object_id || character.current_spawn_object_id || null,
    current_world_id: character.current_world_id || character.world_id || null,
    in_world: character.in_world === true ||
      character.placed_in_world === true ||
      !!character.spawn_object_id ||
      !!character.current_spawn_object_id
  };
};

const extractId = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    const objectIdRegex = /^[0-9a-fA-F]{24}$/;
    const numericRegex = /^\d+$/;

    if (uuidRegex.test(value) || objectIdRegex.test(value) || numericRegex.test(value)) {
      return value;
    }
    return null;
  }

  if (typeof value === 'number') return value.toString();

  if (value && typeof value === 'object') {
    if (value.id !== undefined) return extractId(value.id);
    if (value.character_id !== undefined) return extractId(value.character_id);
    if (value._id !== undefined) return extractId(value._id);
    if (value.image_bit_id !== undefined) return extractId(value.image_bit_id);
  }

  return null;
};

const validateId = (id) => {
  const extracted = extractId(id);
  if (!extracted) return false;

  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  const objectIdRegex = /^[0-9a-fA-F]{24}$/;
  const numericRegex = /^\d+$/;

  return uuidRegex.test(extracted) || objectIdRegex.test(extracted) || numericRegex.test(extracted);
};

const isCharacterPlacedInWorld = (character) => {
  if (!character) return false;

  const spawnObjectId = character.spawn_object_id ||
    character.current_spawn_object_id ||
    character.spawnObjectId ||
    null;

  const currentWorldId = character.current_world_id ||
    character.world_id ||
    character.currentWorldId ||
    null;

  const inWorld = character.in_world === true ||
    character.inWorld === true ||
    character.placed_in_world === true ||
    false;

  const hasSpawnObject = spawnObjectId &&
    spawnObjectId !== 'null' &&
    spawnObjectId !== null &&
    spawnObjectId !== '';

  const hasWorldId = currentWorldId &&
    currentWorldId !== 'null' &&
    currentWorldId !== null &&
    currentWorldId !== '';

  return hasSpawnObject && hasWorldId && inWorld;
};

// ============================================================================
// API: АУТЕНТИФИКАЦИЯ
// ============================================================================

const authAPI = {
  async login(credentials) {
    const flowResponse = await httpClients.imcon.get('/api/login-flow');
    const flowData = flowResponse.data?.data || flowResponse.data;

    if (!flowData || !flowData.id) {
      throw new Error('Failed to get login flow from ImCon');
    }

    let csrfToken = '';
    if (flowData.ui?.nodes) {
      for (const node of flowData.ui.nodes) {
        if (node.attributes?.name === 'csrf_token') {
          csrfToken = node.attributes.value;
          break;
        }
      }
    }

    const loginResponse = await httpClients.imcon.post('/api/login-native', {
      flow_id: flowData.id,
      identifier: credentials.email,
      password: credentials.password,
      csrf_token: csrfToken
    });

    const data = loginResponse.data?.data || loginResponse.data;

    if (!data || !data.session_token) {
      throw new Error('No session_token returned from ImCon');
    }

    return {
      session_token: data.session_token,
      session: data.session,
      identity: data.identity || data.session?.identity
    };
  },

  async register(userData) {
    const response = await httpClients.imcon.post('/api/users', {
      user: {
        email: userData.email,
        password: userData.password,
        name: userData.traits?.name || userData.email.split('@')[0],
        role: 'user'
      }
    });
    return response.data;
  },

  async refreshToken() {
    const response = await httpClients.universe.post('/auth/refresh');
    return response.data;
  },

  async logout() {
    try {
      const response = await httpClients.universe.post('/auth/logout');
      return response.data;
    } finally {
      handleAuthError();
    }
  },
};

// ============================================================================
// API: ADMIN ACCESS (RBAC)
// ============================================================================

const accessAdminAPI = {
  async listUsers(params = {}) {
    const response = await httpClients.imcon.get('/api/access/users', { params });
    return response.data;
  },

  async changeUserRole(kratosId, role, reason = null) {
    const response = await httpClients.imcon.patch(
      `/api/access/users/${kratosId}/role`,
      { role, reason }
    );
    return response.data;
  },

  async listRoles() {
    const response = await httpClients.imcon.get('/api/access/roles');
    return response.data?.data || response.data || [];
  },

  async getUserPermissions(kratosId) {
    const response = await httpClients.imcon.get(
      `/api/access/users/${kratosId}/permissions`
    );
    return response.data;
  },

  async listAudit(params = {}) {
    const response = await httpClients.imcon.get('/api/access/audit', { params });
    return response.data;
  },

  async updateMe(changes) {
    const response = await httpClients.imcon.patch('/api/access/me', changes);
    return response.data;
  },
};

// ============================================================================
// API: ГЕНЕРАЦИЯ
// ============================================================================

const generationAPI = {
  async generateNameAndLegend({ race_id, character_type, gender = 'neutral', traits = {} }) {
    if (!race_id || !character_type) {
      throw new Error('Race ID and character type are required for generation');
    }

    if (!validateCharacterType(character_type)) {
      throw new Error(`Invalid character type: ${character_type}. Allowed types: ${ALLOWED_CHARACTER_TYPES.join(', ')}`);
    }

    const validGenders = ['male', 'female', 'neutral', 'androgynous', 'fluid'];
    if (gender && !validGenders.includes(gender.toLowerCase())) {
      console.warn(`⚠️ Unrecognized gender: ${gender}. Using 'neutral' instead.`);
      gender = 'neutral';
    }

    console.log('🎲 Generating name and legend for character...', { race_id, character_type, gender, traits });

    const generationTraits = { ...traits, gender: traits.gender || gender };

    const response = await httpClients.universe.post('/api/player/characters/generate_background', {
      race_id: race_id,
      character_type: character_type,
      traits: generationTraits
    });

    const data = this.parseGenerationResponse(response.data);

    if (!data || data.status !== 'success') {
      throw new Error(data?.message || 'Failed to generate name and legend');
    }

    return {
      status: 'success',
      data: {
        name: data.data.name || data.data.character_name || '',
        legend: data.data.legend || {},
        gender: data.data.gender || gender
      }
    };
  },

  async generateName({ race_id, character_type, gender = 'neutral', traits = {} }) {
    const result = await this.generateNameAndLegend({ race_id, character_type, gender, traits });
    return { status: result.status, data: { name: result.data.name, gender: result.data.gender } };
  },

  async generateLegend({ race_id, character_type, gender = 'neutral', traits = {} }) {
    const result = await this.generateNameAndLegend({ race_id, character_type, gender, traits });
    return { status: result.status, data: { legend: result.data.legend, gender: result.data.gender } };
  },

  parseGenerationResponse(data) {
    console.log('🔄 Parsing generation response:', data);

    if (!data) throw new Error('Empty response from generation service');

    if (data.status === 'success' && data.data) {
      return { status: 'success', data: this.normalizeGeneratedData(data.data) };
    }

    if (data.data && data.data.status === 'success') {
      return { status: 'success', data: this.normalizeGeneratedData(data.data.data || data.data) };
    }

    if (data.status === 'error') throw new Error(data.message || 'Generation failed');

    const generatedData = this.extractGeneratedData(data);
    if (generatedData) return { status: 'success', data: generatedData };

    return { status: 'success', data: this.normalizeGeneratedData(data) };
  },

  normalizeGeneratedData(data) {
    if (!data || typeof data !== 'object') return {};

    const normalized = { ...data };

    if (normalized.name && !normalized.character_name) normalized.character_name = normalized.name;
    if (!normalized.gender) normalized.gender = 'neutral';

    if (normalized.legend) {
      if (typeof normalized.legend === 'string') {
        normalized.background = normalized.legend;
      } else if (typeof normalized.legend === 'object') {
        Object.entries(normalized.legend).forEach(([key, value]) => {
          if (!normalized[key]) normalized[key] = value;
        });
      }
    }

    const backgroundFields = ['title', 'lineage', 'birthplace', 'childhood_memory', 'motivation', 'background', 'gender'];
    backgroundFields.forEach(field => {
      if (!normalized[field]) normalized[field] = field === 'gender' ? 'neutral' : '';
    });

    return normalized;
  },

  extractGeneratedData(response) {
    if (!response) return null;

    const findData = (obj, depth = 0) => {
      if (depth > 3) return null;
      if (typeof obj !== 'object' || obj === null) return null;

      if (obj.name && (obj.legend || obj.background || obj.title)) return obj;

      for (const key in obj) {
        if (typeof obj[key] === 'object') {
          const found = findData(obj[key], depth + 1);
          if (found) return found;
        }
      }

      return null;
    };

    return findData(response);
  }
};

// ============================================================================
// API: ЧЕРТЕЖИ (SCHEMES)
// ============================================================================

const schemeAPI = {
  async generateAuto(type, params) {
    try {
      console.log(`🎲 Автоматическая генерация ${type}...`, params);
      const response = await httpClients.world.post(`/api/schemes/generate/${type}/auto`, params);
      return this.parseSchemeResponse(response.data, type);
    } catch (error) {
      console.error(`❌ Failed to auto-generate ${type}:`, error);
      throw error;
    }
  },

  async generateSemiAuto(type, baseParams, modules) {
    try {
      console.log(`🎲 Полуавтоматическая генерация ${type} с ${modules.length} модулями...`, baseParams);
      const response = await httpClients.world.post(`/api/schemes/generate/${type}/semi-auto`, {
        base_params: baseParams,
        modules: modules
      });
      return this.parseSchemeResponse(response.data, type);
    } catch (error) {
      console.error(`❌ Failed to semi-auto generate ${type}:`, error);
      throw error;
    }
  },

  async generateManual(type, params, modules) {
    try {
      console.log(`🎲 Ручное создание ${type} с ${modules.length} модулями...`, params);
      const response = await httpClients.world.post(`/api/schemes/generate/${type}/manual`, {
        params: params,
        modules: modules
      });
      return this.parseSchemeResponse(response.data, type);
    } catch (error) {
      console.error(`❌ Failed to manually generate ${type}:`, error);
      throw error;
    }
  },

  async getModuleTemplates(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/module-schemes/templates${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'modules');
    } catch (error) {
      console.error('❌ Failed to get module templates:', error);
      throw error;
    }
  },

  async generateModuleScheme(params) {
    try {
      const response = await httpClients.world.post('/api/module-schemes/generate', params);
      return this.parseSchemeResponse(response.data, 'module');
    } catch (error) {
      console.error('❌ Failed to generate module scheme:', error);
      throw error;
    }
  },

  async listShipSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/ship-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'ships');
    } catch (error) {
      console.error('❌ Failed to list ship schemes:', error);
      throw error;
    }
  },

  async createShipScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/ship-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'ship');
    } catch (error) {
      console.error('❌ Failed to create ship scheme:', error);
      throw error;
    }
  },

  async getShipScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/ship-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'ship');
    } catch (error) {
      console.error(`❌ Failed to get ship scheme ${id}:`, error);
      throw error;
    }
  },

  async updateShipScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/ship-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'ship');
    } catch (error) {
      console.error(`❌ Failed to update ship scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteShipScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/ship-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete ship scheme ${id}:`, error);
      throw error;
    }
  },

  async listStationSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/station-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'stations');
    } catch (error) {
      console.error('❌ Failed to list station schemes:', error);
      throw error;
    }
  },

  async createStationScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/station-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'station');
    } catch (error) {
      console.error('❌ Failed to create station scheme:', error);
      throw error;
    }
  },

  async getStationScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/station-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'station');
    } catch (error) {
      console.error(`❌ Failed to get station scheme ${id}:`, error);
      throw error;
    }
  },

  async updateStationScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/station-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'station');
    } catch (error) {
      console.error(`❌ Failed to update station scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteStationScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/station-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete station scheme ${id}:`, error);
      throw error;
    }
  },

  async listSpaceportSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/spaceport-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'spaceports');
    } catch (error) {
      console.error('❌ Failed to list spaceport schemes:', error);
      throw error;
    }
  },

  async createSpaceportScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/spaceport-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'spaceport');
    } catch (error) {
      console.error('❌ Failed to create spaceport scheme:', error);
      throw error;
    }
  },

  async getSpaceportScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/spaceport-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'spaceport');
    } catch (error) {
      console.error(`❌ Failed to get spaceport scheme ${id}:`, error);
      throw error;
    }
  },

  async updateSpaceportScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/spaceport-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'spaceport');
    } catch (error) {
      console.error(`❌ Failed to update spaceport scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteSpaceportScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/spaceport-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete spaceport scheme ${id}:`, error);
      throw error;
    }
  },

  async listShipyardSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/shipyard-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'shipyards');
    } catch (error) {
      console.error('❌ Failed to list shipyard schemes:', error);
      throw error;
    }
  },

  async createShipyardScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/shipyard-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'shipyard');
    } catch (error) {
      console.error('❌ Failed to create shipyard scheme:', error);
      throw error;
    }
  },

  async getShipyardScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/shipyard-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'shipyard');
    } catch (error) {
      console.error(`❌ Failed to get shipyard scheme ${id}:`, error);
      throw error;
    }
  },

  async updateShipyardScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/shipyard-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'shipyard');
    } catch (error) {
      console.error(`❌ Failed to update shipyard scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteShipyardScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/shipyard-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete shipyard scheme ${id}:`, error);
      throw error;
    }
  },

  async listCivilStructureSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/civil-structure-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'civil_structures');
    } catch (error) {
      console.error('❌ Failed to list civil structure schemes:', error);
      throw error;
    }
  },

  async createCivilStructureScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/civil-structure-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'civil_structure');
    } catch (error) {
      console.error('❌ Failed to create civil structure scheme:', error);
      throw error;
    }
  },

  async getCivilStructureScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/civil-structure-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'civil_structure');
    } catch (error) {
      console.error(`❌ Failed to get civil structure scheme ${id}:`, error);
      throw error;
    }
  },

  async updateCivilStructureScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/civil-structure-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'civil_structure');
    } catch (error) {
      console.error(`❌ Failed to update civil structure scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteCivilStructureScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/civil-structure-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete civil structure scheme ${id}:`, error);
      throw error;
    }
  },

  async listMilitaryStructureSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/military-structure-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'military_structures');
    } catch (error) {
      console.error('❌ Failed to list military structure schemes:', error);
      throw error;
    }
  },

  async createMilitaryStructureScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/military-structure-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'military_structure');
    } catch (error) {
      console.error('❌ Failed to create military structure scheme:', error);
      throw error;
    }
  },

  async getMilitaryStructureScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/military-structure-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'military_structure');
    } catch (error) {
      console.error(`❌ Failed to get military structure scheme ${id}:`, error);
      throw error;
    }
  },

  async updateMilitaryStructureScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/military-structure-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'military_structure');
    } catch (error) {
      console.error(`❌ Failed to update military structure scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteMilitaryStructureScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/military-structure-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete military structure scheme ${id}:`, error);
      throw error;
    }
  },

  async listStructureSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/structure-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'structures');
    } catch (error) {
      console.error('❌ Failed to list structure schemes:', error);
      throw error;
    }
  },

  async listVehicleSchemes(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/vehicle-schemes${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.world.get(url);
      return this.parseSchemeResponse(response.data, 'vehicles');
    } catch (error) {
      console.error('❌ Failed to list vehicle schemes:', error);
      throw error;
    }
  },

  async createVehicleScheme(schemeData) {
    try {
      const response = await httpClients.world.post('/api/vehicle-schemes', schemeData);
      return this.parseSchemeResponse(response.data, 'vehicle');
    } catch (error) {
      console.error('❌ Failed to create vehicle scheme:', error);
      throw error;
    }
  },

  async getVehicleScheme(id) {
    try {
      const response = await httpClients.world.get(`/api/vehicle-schemes/${id}`);
      return this.parseSchemeResponse(response.data, 'vehicle');
    } catch (error) {
      console.error(`❌ Failed to get vehicle scheme ${id}:`, error);
      throw error;
    }
  },

  async updateVehicleScheme(id, updates) {
    try {
      const response = await httpClients.world.put(`/api/vehicle-schemes/${id}`, updates);
      return this.parseSchemeResponse(response.data, 'vehicle');
    } catch (error) {
      console.error(`❌ Failed to update vehicle scheme ${id}:`, error);
      throw error;
    }
  },

  async deleteVehicleScheme(id) {
    try {
      const response = await httpClients.world.delete(`/api/vehicle-schemes/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete vehicle scheme ${id}:`, error);
      throw error;
    }
  },

  parseSchemeResponse(data, type) {
    console.log(`🔄 Parsing ${type} scheme response:`, data);

    if (!data) return null;

    if (data.status === 'success' && data.data) {
      return data.data;
    }

    if (data.data) {
      return data.data;
    }

    if (Array.isArray(data)) {
      return data;
    }

    if (data.items && Array.isArray(data.items)) {
      return data.items;
    }

    if (data.data && data.meta) {
      return data;
    }

    return data;
  },

  calculateSchemeStats(scheme) {
    if (!scheme) return null;

    const modules = scheme.installed_modules || scheme.modules || [];

    const stats = modules.reduce((acc, mod) => {
      acc.mass += (mod.mass_kg || mod.mass || 0);
      acc.cost += (mod.cost || mod.construction_cost || 0);
      acc.powerDraw += (mod.power_draw || mod.power_requirement || 0);
      acc.powerGen += (mod.power_output || 0);
      acc.crew += (mod.crew_capacity || 0);
      acc.cargo += (mod.cargo_capacity || 0);
      acc.combat += (mod.weapon_damage || 0);
      acc.shield += (mod.shield_strength || 0);
      return acc;
    }, { mass: 0, cost: 0, powerDraw: 0, powerGen: 0, crew: 0, cargo: 0, combat: 0, shield: 0 });

    stats.powerBalance = stats.powerGen - stats.powerDraw;

    return stats;
  },

  filterSchemes(schemes, filters = {}) {
    if (!Array.isArray(schemes)) return [];

    return schemes.filter(scheme => {
      if (filters.search && scheme.name) {
        const searchLower = filters.search.toLowerCase();
        if (!scheme.name.toLowerCase().includes(searchLower)) return false;
      }

      if (filters.status && scheme.status !== filters.status) return false;

      if (filters.type && scheme.type !== filters.type) return false;

      if (filters.size_tier && scheme.size_tier !== filters.size_tier) return false;

      if (filters.min_tech_level && (scheme.tech_level || 0) < filters.min_tech_level) return false;
      if (filters.max_tech_level && (scheme.tech_level || 999) > filters.max_tech_level) return false;

      return true;
    });
  }
};

// ============================================================================
// API: АКТИВНЫЕ ПЕРСОНАЖИ
// ============================================================================

const activeCharacterAPI = {
  async getUserActiveCharacters(userId, params = {}) {
    try {
      const response = await httpClients.simCharacter.get(`/api/characters/player/${userId}/active`);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get active characters:', error);
      throw error;
    }
  },

  async getActiveCharacter(characterId) {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/get/basic', {
        character_id: characterId.toString()
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get active character ${characterId}:`, error);
      throw error;
    }
  },

  async activateCharacter(characterId) {
    if (!characterId) throw new Error('characterId is required for activation');

    try {
      const response = await httpClients.simCharacter.post('/api/characters/activate', {
        character_id: characterId.toString()
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to activate character ${characterId}:`, error);
      throw error;
    }
  },

  async deactivateCharacter(characterId) {
    if (!characterId) throw new Error('characterId is required for deactivation');

    try {
      console.log(`⏸️ [GATEWAY] Deactivating character ${characterId}...`);
      const response = await httpClients.simCharacter.post('/api/characters/deactivate', {
        character_id: characterId.toString()
      });
      console.log(`✅ [GATEWAY] Character ${characterId} deactivated successfully`);
      return response.data;
    } catch (error) {
      console.error(`❌ [GATEWAY] Failed to deactivate character ${characterId}:`, error);
      throw error;
    }
  },

  async archiveCharacter(characterId) {
    if (!characterId) throw new Error('characterId is required for archiving');

    try {
      const response = await httpClients.simCharacter.post('/api/characters/archive', {
        character_id: characterId.toString(),
        reason: "manual_deactivation"
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to archive character ${characterId}:`, error);
      throw error;
    }
  },

  async getCharacterFullState(characterId) {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/get/full', {
        character_id: characterId.toString()
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get full state for character ${characterId}:`, error);
      throw error;
    }
  },

  async sendEvent(characterId, event) {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/event', {
        character_id: characterId.toString(),
        event: event
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to send event to character ${characterId}:`, error);
      throw error;
    }
  },

  async placeInSpawnPoint(characterId, spawnObjectId) {
    if (!characterId || !spawnObjectId) throw new Error('characterId and spawnObjectId are required');

    try {
      console.log(`📍 Placing character ${characterId} in spawn point ${spawnObjectId}...`);
      const response = await httpClients.universe.post('/api/player/characters/place-in-spawn-point', {
        character_id: characterId.toString(),
        spawn_object_id: spawnObjectId.toString()
      });
      console.log('✅ Character placed in spawn point successfully');
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to place character in spawn point:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: КОСМИЧЕСКИЕ СТАНЦИИ
// ============================================================================

const spaceStationAPI = {
  async listStations(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/space-stations${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list space stations:', error);
      throw error;
    }
  },

  async getStation(id) {
    try {
      const response = await httpClients.universe.get(`/api/space-stations/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get space station ${id}:`, error);
      throw error;
    }
  },

  async createStation(data) {
    try {
      const response = await httpClients.universe.post('/api/space-stations', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create space station:', error);
      throw error;
    }
  },

  async updateStation(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/space-stations/${id}`, { updates });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update space station ${id}:`, error);
      throw error;
    }
  },

  async deleteStation(id) {
    try {
      const response = await httpClients.universe.delete(`/api/space-stations/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete space station ${id}:`, error);
      throw error;
    }
  },

  async convertToScheme(stationId, params = {}) {
    try {
      const response = await httpClients.universe.post(`/api/space-stations/${stationId}/convert-to-scheme`, params);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert station ${stationId} to scheme:`, error);
      throw error;
    }
  },

  async checkModernization(stationId) {
    try {
      const response = await httpClients.universe.get(`/api/space-stations/${stationId}/modernization`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to check modernization for station ${stationId}:`, error);
      throw error;
    }
  },

  async applyModernization(stationId, modifications, workshopId = null) {
    try {
      const payload = { modifications };
      if (workshopId) payload.workshop_id = workshopId;
      const response = await httpClients.universe.post(`/api/space-stations/${stationId}/modernize`, payload);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to apply modernization to station ${stationId}:`, error);
      throw error;
    }
  },

  async getAvailableModules(stationId) {
    try {
      const response = await httpClients.universe.get(`/api/space-stations/${stationId}/available-modules`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get available modules for station ${stationId}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: КОСМОПОРТЫ
// ============================================================================

const spaceportAPI = {
  async listSpaceports(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/spaceports${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list spaceports:', error);
      throw error;
    }
  },

  async getSpaceport(id) {
    try {
      const response = await httpClients.universe.get(`/api/spaceports/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get spaceport ${id}:`, error);
      throw error;
    }
  },

  async createSpaceport(data) {
    try {
      const response = await httpClients.universe.post('/api/spaceports', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create spaceport:', error);
      throw error;
    }
  },

  async updateSpaceport(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/spaceports/${id}`, { updates });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update spaceport ${id}:`, error);
      throw error;
    }
  },

  async deleteSpaceport(id) {
    try {
      const response = await httpClients.universe.delete(`/api/spaceports/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete spaceport ${id}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: ВЕРФИ
// ============================================================================

const shipyardAPI = {
  async listShipyards(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/shipyards${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list shipyards:', error);
      throw error;
    }
  },

  async getShipyard(id) {
    try {
      const response = await httpClients.universe.get(`/api/shipyards/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get shipyard ${id}:`, error);
      throw error;
    }
  },

  async createShipyard(data) {
    try {
      const response = await httpClients.universe.post('/api/shipyards', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create shipyard:', error);
      throw error;
    }
  },

  async updateShipyard(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/shipyards/${id}`, { updates });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update shipyard ${id}:`, error);
      throw error;
    }
  },

  async deleteShipyard(id) {
    try {
      const response = await httpClients.universe.delete(`/api/shipyards/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete shipyard ${id}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: ГРАЖДАНСКИЕ СТРУКТУРЫ
// ============================================================================

const civilStructureAPI = {
  async listStructures(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/structures${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list civil structures:', error);
      throw error;
    }
  },

  async getStructure(id) {
    try {
      const response = await httpClients.universe.get(`/api/structures/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get civil structure ${id}:`, error);
      throw error;
    }
  },

  async createStructure(data) {
    try {
      const response = await httpClients.universe.post('/api/structures', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create civil structure:', error);
      throw error;
    }
  },

  async updateStructure(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/structures/${id}`, { updates });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update civil structure ${id}:`, error);
      throw error;
    }
  },

  async deleteStructure(id) {
    try {
      const response = await httpClients.universe.delete(`/api/structures/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete civil structure ${id}:`, error);
      throw error;
    }
  },

  async convertToScheme(structureId, params = {}) {
    try {
      const response = await httpClients.universe.post(`/api/structures/${structureId}/modernization/convert-to-scheme`, params);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert civil structure ${structureId} to scheme:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: ВОЕННЫЕ СТРУКТУРЫ
// ============================================================================

const militaryStructureAPI = {
  async listMilitaryStructures(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/military/structures${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list military structures:', error);
      throw error;
    }
  },

  async getMilitaryStructure(id) {
    try {
      const response = await httpClients.universe.get(`/api/military/structures/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get military structure ${id}:`, error);
      throw error;
    }
  },

  async createMilitaryStructure(data) {
    try {
      const response = await httpClients.universe.post('/api/military/structures', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create military structure:', error);
      throw error;
    }
  },

  async updateMilitaryStructure(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/military/structures/${id}`, { updates });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update military structure ${id}:`, error);
      throw error;
    }
  },

  async deleteMilitaryStructure(id) {
    try {
      const response = await httpClients.universe.delete(`/api/military/structures/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete military structure ${id}:`, error);
      throw error;
    }
  },

  async convertToScheme(structureId, params = {}) {
    try {
      const response = await httpClients.universe.post(`/api/military/structures/${structureId}/convert-to-scheme`, params);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert military structure ${structureId} to scheme:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: АКТИВНЫЕ КОРАБЛИ
// ============================================================================

const shipAPI = {
  async listShips(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/ships${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list ships:', error);
      throw error;
    }
  },

  async getShip(id) {
    try {
      const response = await httpClients.universe.get(`/api/ships/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get ship ${id}:`, error);
      throw error;
    }
  },

  async createShip(data) {
    try {
      const response = await httpClients.universe.post('/api/ships', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create ship:', error);
      throw error;
    }
  },

  async updateShip(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/ships/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update ship ${id}:`, error);
      throw error;
    }
  },

  async deleteShip(id) {
    try {
      const response = await httpClients.universe.delete(`/api/ships/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete ship ${id}:`, error);
      throw error;
    }
  },

  async convertToScheme(shipId, params = {}) {
    try {
      const response = await httpClients.universe.post(`/api/ships/${shipId}/convert-to-scheme`, params);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert ship ${shipId} to scheme:`, error);
      throw error;
    }
  },

  async checkModernization(shipId) {
    try {
      const response = await httpClients.universe.get(`/api/ships/${shipId}/modernization`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to check modernization for ship ${shipId}:`, error);
      throw error;
    }
  },

  async applyModernization(shipId, modifications, workshopId = null) {
    try {
      const payload = { modifications };
      if (workshopId) payload.workshop_id = workshopId;
      const response = await httpClients.universe.post(`/api/ships/${shipId}/modernize`, payload);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to apply modernization to ship ${shipId}:`, error);
      throw error;
    }
  },

  async getAvailableModules(shipId) {
    try {
      const response = await httpClients.universe.get(`/api/ships/${shipId}/available-modules`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get available modules for ship ${shipId}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: АКТИВНЫЙ ТРАНСПОРТ
// ============================================================================

const vehicleAPI = {
  async listVehicles(params = {}) {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/api/vehicles${queryParams ? '?' + queryParams : ''}`;
      const response = await httpClients.universe.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to list vehicles:', error);
      throw error;
    }
  },

  async getVehicle(id) {
    try {
      const response = await httpClients.universe.get(`/api/vehicles/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get vehicle ${id}:`, error);
      throw error;
    }
  },

  async createVehicle(data) {
    try {
      const response = await httpClients.universe.post('/api/vehicles', data);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create vehicle:', error);
      throw error;
    }
  },

  async updateVehicle(id, updates) {
    try {
      const response = await httpClients.universe.put(`/api/vehicles/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update vehicle ${id}:`, error);
      throw error;
    }
  },

  async deleteVehicle(id) {
    try {
      const response = await httpClients.universe.delete(`/api/vehicles/${id}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete vehicle ${id}:`, error);
      throw error;
    }
  },

  async convertToScheme(vehicleId, params = {}) {
    try {
      const response = await httpClients.universe.post(`/api/vehicles/${vehicleId}/convert-to-scheme`, params);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to convert vehicle ${vehicleId} to scheme:`, error);
      throw error;
    }
  },

  async getWorkshops(location) {
    try {
      const response = await httpClients.universe.get(`/api/workshops/${location}`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get workshops in ${location}:`, error);
      throw error;
    }
  },

  async getWorkshopServices(workshopId) {
    try {
      const response = await httpClients.universe.get(`/api/workshops/${workshopId}/details`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get services for workshop ${workshopId}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: УПРАВЛЕНИЕ МИРАМИ
// ============================================================================

const worldManagerAPI = {
  getAvailableWorlds: async () => {
    try {
      const response = await httpClients.world.get('/api/worlds');
      const worlds = response.data?.worlds || [];

      if (!Array.isArray(worlds)) {
        console.warn('⚠️ Unexpected response format from /api/worlds, returning empty array');
        return [];
      }

      return worlds;
    } catch (error) {
      console.error('❌ Failed to fetch available worlds:', error);
      if (error.response?.status === 404) return [];
      throw error;
    }
  },

  getSpawnPoints: async (worldId) => {
    try {
      const response = await httpClients.world.get(`/api/worlds/${worldId}/spawn-points`);
      const spawnPoints = response.data?.spawn_points || [];

      if (!Array.isArray(spawnPoints)) {
        console.warn('⚠️ Unexpected response format from /api/worlds/:id/spawn-points, returning empty array');
        return [];
      }

      return spawnPoints;
    } catch (error) {
      console.error(`❌ Failed to fetch spawn points for world ${worldId}:`, error);
      if (error.response?.status === 404) return [];
      throw error;
    }
  },

  getSpawnPointInfo: async (spawnId) => {
    try {
      const response = await httpClients.world.get(`/api/spawn-points/${spawnId}`);
      return response.data?.spawn_point || response.data || null;
    } catch (error) {
      console.error(`❌ Failed to fetch spawn point info for ${spawnId}:`, error);
      if (error.response?.status === 404) return null;
      throw error;
    }
  },

  getBuildingInfo: async (buildingId) => {
    try {
      const response = await httpClients.world.get(`/api/buildings/${buildingId}`);
      return response.data?.building || response.data || null;
    } catch (error) {
      console.error(`❌ Failed to fetch building info for ${buildingId}:`, error);
      if (error.response?.status === 404) return null;
      throw error;
    }
  }
};

// ============================================================================
// API: СОЗДАНИЕ ПЕРСОНАЖЕЙ (DESIGN)
// ============================================================================

const designCharacterAPI = {
  async getDesignCharacters(userId, params = {}) {
    try {
      const response = await httpClients.simCharacter.get(`/api/characters/player/${userId}`);
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get design characters:', error);
      throw error;
    }
  },

  async createDesignCharacter(characterData) {
    try {
      if (characterData.character_type && !validateCharacterType(characterData.character_type)) {
        throw new Error(`Invalid character type: ${characterData.character_type}`);
      }

      const response = await httpClients.simCharacter.post('/api/characters/design', {
        character: characterData
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create design character:', error);
      throw error;
    }
  },

  async updateDesignCharacter(characterId, updates) {
    try {
      const response = await httpClients.simCharacter.put('/api/characters/update/collection', {
        character_id: characterId.toString(),
        updates: updates
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to update design character ${characterId}:`, error);
      throw error;
    }
  },

  async deleteDesignCharacter(characterId) {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/archive', {
        character_id: characterId.toString(),
        reason: "design_cancellation"
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to delete design character ${characterId}:`, error);
      throw error;
    }
  }
};

// ============================================================================
// API: NPC
// ============================================================================

const npcAPI = {
  async createNPC(npcData) {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/npc', {
        npc: npcData
      });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to create NPC:', error);
      throw error;
    }
  }
};

// ============================================================================
// API: ОНТОЛОГИЯ
// ============================================================================

const ontologyAPI = {
  async get(type, params = {}) {
    try {
      console.log(`📥 Gateway: Запрос онтологии ${type}...`);
      const response = await httpClients.imcon.get(`/api/ontology/${type}`, { params });
      const parsedData = this.parseOntologyResponse(response.data, type);
      console.log(`✅ Gateway: ${parsedData.length} элементов ${type} получено`);
      return parsedData;
    } catch (error) {
      console.error(`❌ Gateway: Ошибка получения ${type}:`, error);
      throw error;
    }
  },

  async getById(type, id, params = {}) {
    try {
      console.log(`📥 Gateway: Запрос элемента ${type}/${id}...`);
      const response = await httpClients.imcon.get(`/api/ontology/${type}/${id}`, { params });
      const parsedItem = this.parseSingleItem(response.data, type);
      console.log(`✅ Gateway: Элемент ${type}/${id} получен`);
      return parsedItem;
    } catch (error) {
      console.error(`❌ Gateway: Ошибка получения ${type}/${id}:`, error);
      throw error;
    }
  },

  async getAllForCharacterCreation() {
    const types = ['races', 'character_templates', 'skills', 'traits', 'preferences', 'phobias'];

    try {
      const promises = types.map(type => this.get(type));
      const results = await Promise.all(promises);

      const ontologyData = types.reduce((acc, type, index) => {
        acc[type] = results[index];
        console.log(`📊 ${type}: ${results[index].length} элементов загружено`);
        return acc;
      }, {});

      console.log('✅ Все онтологии загружены для создания персонажа');
      return ontologyData;
    } catch (error) {
      console.error('❌ Ошибка загрузки онтологий:', error);
      throw error;
    }
  },

  async getRacesAndTemplates() {
    try {
      const [races, templates] = await Promise.all([this.get('races'), this.get('character_templates')]);
      console.log(`📊 Races: ${races.length}, Templates: ${templates.length} загружено`);
      return { races, character_templates: templates };
    } catch (error) {
      console.error('❌ Ошибка загрузки рас и шаблонов:', error);
      throw error;
    }
  },

  parseOntologyResponse(data, type) {
    console.log(`🔄 Gateway: Парсинг ответа для ${type}`, data);

    if (!data) return [];
    if (Array.isArray(data)) return this.normalizeItems(data, type);
    if (data.data && Array.isArray(data.data)) return this.normalizeItems(data.data, type);
    if (data.items && Array.isArray(data.items)) return this.normalizeItems(data.items, type);
    if (data.data && data.data.items && Array.isArray(data.data.items)) return this.normalizeItems(data.data.items, type);
    if (data[type] && Array.isArray(data[type])) return this.normalizeItems(data[type], type);

    const typeKey = Object.keys(data).find(key =>
      key.toLowerCase().includes(type.toLowerCase()) && Array.isArray(data[key])
    );
    if (typeKey) return this.normalizeItems(data[typeKey], type);

    if (type === 'skills' && data.data && data.data.categories && data.data.skills && Array.isArray(data.data.skills)) {
      return this.normalizeItems(data.data.skills, type);
    }

    if (type === 'skills' && data.categories && data.skills) return this.normalizeItems(data.skills, type);

    if (data.data && typeof data.data === 'object') {
      if (data.data[type] && Array.isArray(data.data[type])) return this.normalizeItems(data.data[type], type);
      const arrayKey = Object.keys(data.data).find(key => Array.isArray(data.data[key]));
      if (arrayKey) return this.normalizeItems(data.data[arrayKey], type);
    }

    const findDeepArray = (obj) => {
      if (Array.isArray(obj)) return obj;
      if (typeof obj === 'object' && obj !== null) {
        for (const key in obj) {
          const result = findDeepArray(obj[key]);
          if (result) return result;
        }
      }
      return null;
    };

    const deepArray = findDeepArray(data);
    if (deepArray) return this.normalizeItems(deepArray, type);

    console.warn(`⚠️ Gateway: Неизвестный формат данных для ${type}:`, data);
    return [];
  },

  parseSingleItem(data, type) {
    console.log(`🔄 Gateway: Парсинг одиночного элемента ${type}`, data);

    if (!data) return null;

    if (typeof data === 'object' && !Array.isArray(data)) {
      if (data.data && typeof data.data === 'object') return this.normalizeItem(data.data, type);
      if (data.item && typeof data.item === 'object') return this.normalizeItem(data.item, type);
      return this.normalizeItem(data, type);
    }

    if (Array.isArray(data) && data.length > 0) return this.normalizeItem(data[0], type);

    return null;
  },

  normalizeItems(items, type) {
    if (!Array.isArray(items)) return [];
    return items.map(item => this.normalizeItem(item, type));
  },

  normalizeItem(item, type) {
    if (!item || typeof item !== 'object') return item;

    const normalized = { ...item };

    if (!normalized.id && normalized._id) normalized.id = normalized._id;
    if (!normalized.id && normalized.bit_id) normalized.id = `bit_${normalized.bit_id}`;
    if (!normalized.name && normalized.id) normalized.name = normalized.id;

    switch (type) {
      case 'races': return this.normalizeRace(item);
      case 'character_templates': return this.normalizeTemplate(item);
      case 'skills': return this.normalizeSkill(item);
      case 'traits': return this.normalizeTrait(item);
      case 'preferences': return this.normalizePreference(item);
      case 'phobias': return this.normalizePhobia(item);
      default: return normalized;
    }
  },

  normalizeRace(race) {
    return {
      id: race.id || race._id || `race_${race.bit_id || race.name}`,
      name: race.name || 'Без названия',
      description: race.description || race.biology?.description || 'Описание отсутствует',
      environment: race.environment || 'surface',
      technology_level: race.technology_level || 1,
      home_system: race.home_system || 'Неизвестная система',
      biology: race.biology || {},
      psychology: race.psychology || {},
      race_compatibility: race.race_compatibility || [],
      compatible_templates: race.compatible_templates || [],
      image_url: race.image_url || null,
      lore: race.lore || ''
    };
  },

  normalizeTemplate(template) {
    let goals = template.goals && Array.isArray(template.goals) ? template.goals :
      (template.data?.goals && Array.isArray(template.data.goals) ? template.data.goals : []);

    let primary_skills = template.primary_skills && Array.isArray(template.primary_skills) ? template.primary_skills :
      (template.data?.primary_skills && Array.isArray(template.data.primary_skills) ? template.data.primary_skills : []);

    let race_compatibility = template.race_compatibility && Array.isArray(template.race_compatibility) ? template.race_compatibility :
      (template.data?.race_compatibility && Array.isArray(template.data.race_compatibility) ? template.data.race_compatibility : []);

    return {
      id: template.id || template._id || `tmpl_${template.bit_id || template.name}`,
      name: template.name || 'Без названия',
      description: template.description || template.data?.description || '',
      goals,
      primary_skills,
      race_compatibility,
      temperament: template.temperament || template.data?.temperament || 'adaptive',
      goals_display: goals,
      skills_display: primary_skills,
      races_display: race_compatibility,
      icon: this.getTemplateIcon(template.id)
    };
  },

  normalizeSkill(skill) {
    return {
      id: skill.id || skill._id || `skill_${skill.bit_id || skill.name}`,
      name: skill.name || 'Без названия',
      description: skill.description || '',
      category: skill.category || 'general',
      mastery_levels: skill.mastery_levels || {},
      compatible_races: skill.compatible_races || [],
      compatible_environments: skill.compatible_environments || [],
      requires: skill.requires || [],
      actions: skill.actions || skill.data?.actions || [],
      bit_id: skill.bit_id || null,
      icon: this.getSkillIcon(skill.category)
    };
  },

  normalizeTrait(trait) {
    const traitData = trait.data || trait;
    return {
      id: trait.id || traitData.id || `trait_${trait.bit_id || traitData.name}`,
      name: trait.name || traitData.name || 'Без названия',
      description: trait.description || traitData.description || '',
      type: trait.type || traitData.type || 'general',
      category: trait.category || traitData.category || 'volitional',
      intensity_levels: trait.intensity_levels || traitData.intensity_levels || {},
      compatible_roles: trait.compatible_roles || traitData.compatible_roles || [],
      requires: trait.requires || traitData.requires || []
    };
  },

  normalizePreference(preference) {
    const prefData = preference.data || preference;
    return {
      id: preference.id || prefData.id || `pref_${preference.bit_id || prefData.name}`,
      name: preference.name || prefData.name || 'Без названия',
      description: preference.description || prefData.description || '',
      type: preference.type || prefData.type || 'sensory',
      category: preference.category || prefData.category || 'sensory',
      polarity: preference.polarity || prefData.polarity || {},
      intensity_levels: preference.intensity_levels || prefData.intensity_levels || {},
      manifestations: preference.manifestations || prefData.manifestations || {}
    };
  },

  normalizePhobia(phobia) {
    const phobiaData = phobia.data || phobia;
    return {
      id: phobia.id || phobiaData.id || `phob_${phobia.bit_id || phobiaData.name}`,
      name: phobia.name || phobiaData.name || 'Без названия',
      description: phobia.description || phobiaData.description || '',
      type: phobia.type || phobiaData.type || 'biological',
      category: phobia.category || phobiaData.category || 'biological',
      polarity: phobia.polarity || phobiaData.polarity || {},
      severity_levels: phobia.severity_levels || phobiaData.severity_levels || {},
      symptoms: phobia.symptoms || phobiaData.symptoms || {},
      triggers: phobia.triggers || phobiaData.triggers || [],
      treatment_approaches: phobia.treatment_approaches || phobiaData.treatment_approaches || []
    };
  },

  getTemplateIcon(templateId) {
    const iconMap = {
      'explorer': '🧭', 'warrior': '⚔️', 'diplomat': '🤝', 'scientist': '🔬',
      'engineer': '⚙️', 'pilot': '🚀', 'medic': '🩺', 'entrepreneur': '💰',
      'hunter': '🏹', 'farmer': '🌾', 'creator': '🎨', 'mystic': '🔮'
    };
    return iconMap[templateId] || '👤';
  },

  getSkillIcon(category) {
    const iconMap = {
      'combat': '⚔️', 'social': '💬', 'production': '🏭', 'science': '🔬',
      'survival': '🏕️', 'creative': '🎨', 'technical': '⚙️'
    };
    return iconMap[category] || '📚';
  },

  async search(type, query, params = {}) {
    try {
      const response = await httpClients.imcon.post(`/api/ontology/${type}/search`, { query, ...params });
      return this.parseOntologyResponse(response.data, type);
    } catch (error) {
      console.error(`❌ Ошибка поиска ${type}:`, error);
      throw error;
    }
  },

  async checkCompatibility(sourceType, sourceId, targetType, targetId) {
    try {
      const response = await httpClients.imcon.post('/api/ontology/compatibility/check', {
        source_type: sourceType, source_id: sourceId, target_type: targetType, target_id: targetId
      });
      return response.data;
    } catch (error) {
      console.error('❌ Ошибка проверки совместимости:', error);
      return { compatible: true, reason: 'Compatibility check unavailable' };
    }
  }
};

// ============================================================================
// API: ОБЪЕДИНЁННЫЙ ДЛЯ ПЕРСОНАЖЕЙ
// ============================================================================

const characterAPI = {
  async getCharacters(type = CHARACTER_TYPES.ACTIVE, params = {}) {
    const userId = params.userId || (store.getState()?.auth?.user?.id);
    if (!userId) throw new Error('User ID is required');

    switch (type) {
      case CHARACTER_TYPES.ACTIVE: return activeCharacterAPI.getUserActiveCharacters(userId, params);
      case CHARACTER_TYPES.DESIGN: return designCharacterAPI.getDesignCharacters(userId, params);
      default: throw new Error(`Unknown character type: ${type}`);
    }
  },

  async getCharactersByPlayer(userId, params = {}) {
    try {
      const response = await httpClients.simCharacter.get(`/api/characters/player/${userId}`, { params });
      return response.data;
    } catch (error) {
      console.error('❌ Failed to get characters for player:', error);
      throw error;
    }
  },

  async getGameAuthToken(characterId) {
    try {
      if (!characterId) throw new Error('Character ID is required');
      console.log('🎮 [Gateway] Getting game auth token for character:', characterId);

      const response = await httpClients.universe.post('/api/game/auth', {
        character_id: characterId.toString()
      });

      console.log('✅ [Gateway] Game auth token received');
      return response.data;
    } catch (error) {
      console.error('❌ [Gateway] Failed to get game auth token:', error);
      throw error;
    }
  },

  async getCharacter(characterId, type = 'auto') {
    try {
      const response = await httpClients.simCharacter.post('/api/characters/get/basic', {
        character_id: characterId.toString()
      });

      const character = response.data || response;
      return normalizeCharacterPlacement(character);
    } catch (error) {
      console.error(`❌ Failed to get character ${characterId}:`, error);
      throw error;
    }
  },

  async createCharacter(characterData) {
    if (characterData.is_npc) return npcAPI.createNPC(characterData);
    return designCharacterAPI.createDesignCharacter(characterData);
  },

  async updateCharacter(characterId, updates, type = 'auto') {
    const response = await httpClients.simCharacter.put('/api/characters/update/collection', {
      character_id: characterId.toString(), updates: updates
    });
    return response.data;
  },

  async deleteCharacter(characterId, type = 'auto') {
    const response = await httpClients.simCharacter.post('/api/characters/archive', {
      character_id: characterId.toString(),
      reason: type === 'active' ? 'game_completion' : 'design_cancellation'
    });
    return response.data;
  }
};

// ============================================================================
// API: ИГРОВЫЕ ОПЕРАЦИИ (WebSocket + HTTP)
// ============================================================================

const gameAPI = {
  async connectToGame(characterId, options = {}) {
    if (!characterId) throw new Error('Character ID is required to connect to game');

    console.log('🎮 Connecting to game server...', { characterId });

    try {
      const characterData = await activeCharacterAPI.getActiveCharacter(characterId);
      console.log('🎮 Requesting game auth token...');

      const authResponse = await httpClients.universe.post('/api/game/auth', {
        character_id: characterId.toString()
      });

      const responseData = authResponse.data || authResponse;
      const authData = responseData.data || responseData;
      const { token, server_url, expires_at } = authData;

      console.log('🎮 Game auth response:', {
        hasToken: !!token, server_url, expires_at,
        fullResponse: JSON.stringify(responseData, null, 2)
      });

      if (!token || !server_url) {
        console.error('❌ Invalid game authentication response:', authResponse.data || authResponse);
        throw new Error('Invalid game authentication response: missing token or server_url');
      }

      const socketUrl = buildWebSocketUrl(server_url, '/socket');
      console.log('🔌 WebSocket URL:', socketUrl);

      const maxRetries = options.maxRetries || 3;
      let retryDelay = options.retryDelay || 2000;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        let socket = null;
        let channel = null;

        try {
          console.log(`🔄 Game connection attempt ${attempt}/${maxRetries}`);

          socket = new Socket(socketUrl, {
            params: { token, character_id: characterId, vsn: "2.0.0" },
            reconnectAfterMs: options.reconnect !== false ?
              (tries) => Math.min(1000 * Math.pow(2, tries), 30000) : false,
            timeout: options.timeout || 10000,
          });

          channel = socket.channel(`game:${characterId}`, {
            token: token,
            client_version: "1.0.0",
            timestamp: Date.now(),
            attempt: attempt
          });

          await new Promise((resolve, reject) => {
            socket.onOpen(() => {
              console.log(`✅ Socket connected on attempt ${attempt}`);
              resolve();
            });

            socket.onError((error) => {
              console.error(`❌ Socket error on attempt ${attempt}:`, error);
              reject(new Error(`Socket connection failed: ${error.message || 'Unknown error'}`));
            });

            socket.connect();

            setTimeout(() => reject(new Error('Socket connection timeout')), options.timeout || 10000);
          });

          console.log(`🔄 Game channel join attempt ${attempt}/${maxRetries}`);

          const joinResponse = await new Promise((resolve, reject) => {
            channel.join()
              .receive("ok", (response) => {
                console.log(`✅ Channel join ok on attempt ${attempt}:`, response);
                resolve(response);
              })
              .receive("error", (resp) => {
                console.error(`❌ Channel join error on attempt ${attempt}:`, resp);
                reject(new Error(resp.reason || resp.message || 'Join failed'));
              })
              .receive("timeout", () => {
                console.error(`❌ Channel join timeout on attempt ${attempt}`);
                reject(new Error('Join timeout'));
              });
          });

          console.log('✅ Successfully joined game channel');

          if (typeof localStorage !== 'undefined') {
            localStorage.setItem('game_auth_token', token);
          }

          return {
            socket,
            channel,
            token,
            server_url,
            expires_at,
            characterId,
            characterData: characterData.data,
            async join() { return joinResponse; },
            disconnect: () => {
              console.log('🔌 Disconnecting from game...');
              if (channel) channel.leave();
              if (socket) socket.disconnect();

              if (typeof localStorage !== 'undefined') {
                localStorage.removeItem('game_auth_token');
              }
            }
          };
        } catch (error) {
          console.error(`❌ Connection attempt ${attempt} failed:`, error.message);

          if (channel) { try { channel.leave(); } catch (e) {} }
          if (socket) { try { socket.disconnect(); } catch (e) {} }

          if (attempt === maxRetries) {
            throw new Error(`Failed to connect after ${maxRetries} attempts. Last error: ${error.message}`);
          }

          console.log(`⏳ Waiting ${retryDelay}ms before retry ${attempt + 1}...`);
          await new Promise(resolve => setTimeout(resolve, retryDelay));

          if (options.useExponentialBackoff) {
            retryDelay = Math.min(retryDelay * 2, 30000);
          }
        }
      }

      throw new Error('Failed to establish game connection');
    } catch (error) {
      console.error('❌ Failed to connect to game:', error);

      if (error.response) {
        console.error('Response data:', error.response.data);
        console.error('Response status:', error.response.status);
      }

      throw new Error(`Game connection failed: ${error.message}`);
    }
  },

  async getCharacterState(characterId) {
    try {
      return await activeCharacterAPI.getCharacterFullState(characterId);
    } catch (error) {
      console.error(`❌ Failed to get character state for ${characterId}:`, error);
      throw error;
    }
  },

  async performAction(characterId, action, params = {}) {
    try {
      const response = await httpClients.simCharacter.post('/api/dialogue/quick-message', {
        character_id: characterId.toString(), action, params
      });
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to perform action for ${characterId}:`, error);
      throw error;
    }
  },

  async getInventory(characterId) {
    try {
      const response = await httpClients.universe.get(`/api/player/characters/${characterId}/inventory`);
      return response.data;
    } catch (error) {
      console.error(`❌ Failed to get inventory for ${characterId}:`, error);
      throw error;
    }
  },

  async getCharacterGoals(characterId) {
    try {
      console.log(`🎯 [Gateway] Getting goals for character ${characterId}...`);
      const response = await httpClients.universe.get(`/api/player/characters/${characterId}/goals`);
      const data = response?.data || response;
      const goals = data?.goals || [];
      console.log(`✅ [Gateway] Loaded ${goals.length} goals for character ${characterId}`);
      return goals;
    } catch (error) {
      console.warn(`⚠️ [Gateway] Failed to load goals for ${characterId}:`, error);
      return [];
    }
  },

  async getCharacterTasks(characterId) {
    try {
      console.log(`📋 [Gateway] Getting tasks for character ${characterId}...`);
      const response = await httpClients.universe.get(`/api/player/characters/${characterId}/tasks`);
      const data = response?.data || response;
      const tasks = data?.tasks || [];
      console.log(`✅ [Gateway] Loaded ${tasks.length} tasks for character ${characterId}`);
      return tasks;
    } catch (error) {
      console.warn(`⚠️ [Gateway] Failed to load tasks for ${characterId}:`, error);
      return [];
    }
  },

  async sendStimulus(characterId, stimulusData) {
    try {
      console.log(`🧠 [Gateway] Sending stimulus to character ${characterId}...`, stimulusData);
      const response = await httpClients.universe.post(`/api/player/characters/${characterId}/stimulus`, {
        stimulus: stimulusData
      });
      console.log(`✅ [Gateway] Stimulus sent to character ${characterId}`);
      return response?.data || response;
    } catch (error) {
      console.error(`❌ [Gateway] Failed to send stimulus to ${characterId}:`, error);
      throw error;
    }
  },

  async getCharacterEmotions(characterId) {
    try {
      console.log(`😊 [Gateway] Getting emotions for character ${characterId}...`);
      const response = await httpClients.universe.get(`/api/player/characters/${characterId}/emotions`);
      const data = response?.data || response;
      const emotions = data?.emotions || {};
      console.log(`✅ [Gateway] Loaded emotions for character ${characterId}`);
      return emotions;
    } catch (error) {
      console.warn(`⚠️ [Gateway] Failed to load emotions for ${characterId}:`, error);
      return {};
    }
  }
};

// ============================================================================
// WEBSOCKET ПОДКЛЮЧЕНИЯ (ОБЩИЕ)
// ============================================================================

const connectSocket = (serviceKey, options = {}) => {
  const { playerId, token, onOpen, onError, onClose, reconnect = true } = options;

  const serviceUrls = {
    universe_gateway: SERVICE_URLS.universe_gateway,
    imcon: SERVICE_URLS.imcon,
    world_manager: SERVICE_URLS.world_manager,
    sim_character: SERVICE_URLS.sim_character,
    character_designer: SERVICE_URLS.character_designer,
  };

  const baseUrl = serviceUrls[serviceKey];
  if (!baseUrl) throw new Error(`Unknown service for socket: ${serviceKey}`);

  const socketUrl = buildWebSocketUrl(baseUrl, '/socket');

  const socketParams = {
    vsn: "2.0.0",
    ...(token && { token }),
    ...(playerId && { player_id: playerId })
  };

  console.log(`🔌 Connecting to ${serviceKey} socket:`, { socketUrl });

  const socket = new Socket(socketUrl, {
    params: socketParams,
    reconnectAfterMs: reconnect ? (tries) => Math.min(1000 * Math.pow(2, tries), 30000) : false,
    timeout: 10000,
    logger: (kind, msg, data) => {
      if (kind === 'error') console.error(`🔌 [${serviceKey} Socket ${kind}]:`, msg, data);
    }
  });

  socket.onOpen(() => {
    console.log(`✅ Socket connected to ${serviceKey}`);
    if (onOpen) onOpen();
  });

  socket.onError((error) => {
    console.error(`❌ Socket error for ${serviceKey}:`, error);
    if (onError) onError(error);
  });

  socket.onClose((event) => {
    console.log(`🔌 Socket disconnected from ${serviceKey}:`, event);
    if (onClose) onClose(event);
  });

  socket.connect();

  return socket;
};

// ============================================================================
// УТИЛИТЫ ДЛЯ ПЕРСОНАЖЕЙ
// ============================================================================

const characterUtils = {
  CHARACTER_TYPES,
  ALLOWED_CHARACTER_TYPES,
  validateCharacterType,

  determineCharacterType: (character) => {
    if (!character) return CHARACTER_TYPES.DESIGN;
    if (character.in_world === true || character.is_active === true) return CHARACTER_TYPES.ACTIVE;
    if (character.created_at && !character.in_world) return CHARACTER_TYPES.DESIGN;
    return CHARACTER_TYPES.DESIGN;
  },

  extractDataFromResponse: (response, dataType) => {
    const data = response.data || response;

    if (Array.isArray(data)) return data;

    if (data && typeof data === 'object') {
      if (dataType === 'characters' && data.characters) return data.characters;
      if (data.data) return Array.isArray(data.data) ? data.data : [data.data];
      if (data.items) return data.items;
    }

    return [];
  },

  hasCharacters: (charactersResponse, type = CHARACTER_TYPES.ACTIVE) => {
    const characters = characterUtils.extractDataFromResponse(charactersResponse, 'characters');
    const validCharacters = characters.filter(char => char && char.id);

    if (type === CHARACTER_TYPES.ACTIVE) {
      return validCharacters.some(char => char.in_world === true);
    } else if (type === CHARACTER_TYPES.DESIGN) {
      return validCharacters.some(char => char.in_world !== true);
    }

    return validCharacters.length > 0;
  },

  getSelectedCharacterId: () => {
    if (typeof window === 'undefined') return null;
    try {
      return localStorage.getItem('selected_character_id');
    } catch (error) {
      console.warn('Error getting selected character from localStorage:', error);
      return null;
    }
  },

  setSelectedCharacterId: (characterId) => {
    if (typeof window === 'undefined') return;
    try {
      if (characterId) {
        localStorage.setItem('selected_character_id', characterId);
      } else {
        localStorage.removeItem('selected_character_id');
      }
    } catch (error) {
      console.warn('Error saving selected character to localStorage:', error);
    }
  },

  clearCharacterData: () => {
    if (typeof window === 'undefined') return;
    try {
      localStorage.removeItem('selected_character_id');
      localStorage.removeItem('character_data');
      localStorage.removeItem('last_created_character');
    } catch (error) {
      console.warn('Error clearing character data from localStorage:', error);
    }
  },

  formatLegendForDisplay: (legend) => {
    if (!legend || typeof legend !== 'object') return '';

    const displayText = [
      legend.title && `**Звание:** ${legend.title}`,
      legend.lineage && `**Род:** ${legend.lineage}`,
      legend.birthplace && `**Родина:** ${legend.birthplace}`,
      legend.childhood_memory && `**Память детства:** ${legend.childhood_memory}`,
      legend.motivation && `**Мотивация:** ${legend.motivation}`,
      legend.background && `**Предыстория:** ${legend.background}`
    ]
      .filter(Boolean)
      .join('\n');

    return displayText || 'Легенда не сгенерирована';
  },

  createBackgroundObject: (name, legend, gender = 'neutral') => {
    return {
      name, gender, ...legend,
      description: legend.childhood_memory || legend.background || '',
      generated: true
    };
  },

  getLocationInfoFromPosition: (position) => {
    if (!position) return null;

    const locationInfo = position.location_info || {};
    const location = position.location || locationInfo.id || 'unknown';
    const locationName = locationInfo.name || position.location_name || 'Unknown Location';
    const locationType = locationInfo.type || position.location_type || 'unknown';
    const description = locationInfo.description || position.location_description || '';

    return {
      id: location,
      name: locationName,
      type: locationType,
      description: description,
      npcs: locationInfo.npcs || [],
      facilities: locationInfo.facilities || [],
      environment: locationInfo.environment || {}
    };
  },

  formatLocation: (position) => {
    const info = characterUtils.getLocationInfoFromPosition(position);
    if (!info) return 'Неизвестная локация';
    return info.name;
  }
};

// ============================================================================
// УТИЛИТЫ ДЛЯ ОНТОЛОГИЙ
// ============================================================================

const ontologyUtils = {
  filterByRaceCompatibility: (items, raceId, compatibilityField = 'race_compatibility') => {
    if (!raceId) return items;
    return items.filter(item => {
      const compatibleRaces = item[compatibilityField];
      return !compatibleRaces || compatibleRaces.length === 0 || compatibleRaces.includes(raceId);
    });
  },

  groupByCategory: (items, categoryField = 'category') => {
    return items.reduce((groups, item) => {
      const category = item[categoryField] || 'other';
      if (!groups[category]) groups[category] = [];
      groups[category].push(item);
      return groups;
    }, {});
  },

  search: (items, query) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item =>
      item.name?.toLowerCase().includes(lowerQuery) ||
      item.description?.toLowerCase().includes(lowerQuery) ||
      item.id?.toLowerCase().includes(lowerQuery)
    );
  },

  sortByLevel: (items, levelField = 'level') => {
    return [...items].sort((a, b) => {
      const aLevel = a[levelField] || 0;
      const bLevel = b[levelField] || 0;
      return aLevel - bLevel;
    });
  },

  getIconForType: (type) => {
    const iconMap = {
      races: '👽', skills: '⚡', character_templates: '📋', traits: '⭐',
      preferences: '❤️', phobias: '😱', environments: '🌍', factions: '🏛️'
    };
    return iconMap[type] || '📄';
  },

  formatDescription: (description, maxLength = 150) => {
    if (!description) return '';
    if (description.length <= maxLength) return description;
    return description.substring(0, maxLength - 3) + '...';
  },

  checkRaceTemplateCompatibility: (raceId, template) => {
    if (!raceId || !template) return true;
    const compatibleRaces = template.race_compatibility || [];
    return compatibleRaces.length === 0 || compatibleRaces.includes(raceId);
  }
};

// ============================================================================
// УТИЛИТЫ ДЛЯ ЧЕРТЕЖЕЙ
// ============================================================================

const schemeUtils = {
  SCHEME_TYPES,

  getIconForSchemeType(type) {
    const iconMap = {
      [SCHEME_TYPES.SHIP]: '🚀',
      [SCHEME_TYPES.STRUCTURE]: '🏗️',
      [SCHEME_TYPES.VEHICLE]: '🚜',
      [SCHEME_TYPES.MODULE]: '🧩',
      [SCHEME_TYPES.STATION]: '🛰️',
      [SCHEME_TYPES.SPACEPORT]: '🚀',
      [SCHEME_TYPES.SHIPYARD]: '🏭',
      [SCHEME_TYPES.CIVIL_STRUCTURE]: '🏘️',
      [SCHEME_TYPES.MILITARY_STRUCTURE]: '🏛️'
    };
    return iconMap[type] || '📐';
  },

  getColorForSchemeType(type) {
    const colorMap = {
      [SCHEME_TYPES.SHIP]: '#1976d2',
      [SCHEME_TYPES.STRUCTURE]: '#388e3c',
      [SCHEME_TYPES.VEHICLE]: '#f57c00',
      [SCHEME_TYPES.MODULE]: '#7b1fa2',
      [SCHEME_TYPES.STATION]: '#0288d1',
      [SCHEME_TYPES.SPACEPORT]: '#d32f2f',
      [SCHEME_TYPES.SHIPYARD]: '#7b1fa2',
      [SCHEME_TYPES.CIVIL_STRUCTURE]: '#388e3c',
      [SCHEME_TYPES.MILITARY_STRUCTURE]: '#f57c00'
    };
    return colorMap[type] || '#757575';
  },

  getStatusLabel(status) {
    const statusMap = {
      draft: 'Черновик',
      active: 'Активен',
      deprecated: 'Устарел',
      archived: 'В архиве',
      testing: 'Тестируется'
    };
    return statusMap[status] || status || 'Неизвестно';
  },

  formatSizeTier(tier) {
    const tierMap = {
      tiny: 'Крошечный',
      small: 'Малый',
      medium: 'Средний',
      large: 'Большой',
      huge: 'Огромный',
      colossal: 'Колоссальный'
    };
    return tierMap[tier] || tier || 'Не указан';
  },

  calculateTotalCost(scheme) {
    if (!scheme) return 0;
    const modules = scheme.installed_modules || scheme.modules || [];
    const baseCost = scheme.base_cost || scheme.hull_cost || 0;
    const modulesCost = modules.reduce((sum, mod) => sum + (mod.cost || mod.construction_cost || 0), 0);
    return baseCost + modulesCost;
  },

  calculateTotalMass(scheme) {
    if (!scheme) return 0;
    const modules = scheme.installed_modules || scheme.modules || [];
    const baseMass = scheme.hull_mass || scheme.base_mass || 0;
    const modulesMass = modules.reduce((sum, mod) => sum + (mod.mass || mod.mass_kg || 0), 0);
    return baseMass + modulesMass;
  },

  getRequiredCrew(scheme) {
    if (!scheme) return { min: 0, optimal: 0, max: 0 };
    const modules = scheme.installed_modules || scheme.modules || [];
    const baseCrew = scheme.base_crew || { min: 0, optimal: 0, max: 0 };

    const modulesCrew = modules.reduce((acc, mod) => {
      const crewReq = mod.crew_required || mod.crew_capacity || { min: 0, optimal: 0, max: 0 };
      if (typeof crewReq === 'number') {
        acc.min += crewReq;
        acc.optimal += crewReq;
        acc.max += crewReq;
      } else {
        acc.min += crewReq.min || 0;
        acc.optimal += crewReq.optimal || 0;
        acc.max += crewReq.max || 0;
      }
      return acc;
    }, { min: 0, optimal: 0, max: 0 });

    return {
      min: baseCrew.min + modulesCrew.min,
      optimal: baseCrew.optimal + modulesCrew.optimal,
      max: baseCrew.max + modulesCrew.max
    };
  },

  getPowerStats(scheme) {
    if (!scheme) return { draw: 0, gen: 0, balance: 0 };
    const modules = scheme.installed_modules || scheme.modules || [];

    const stats = modules.reduce((acc, mod) => {
      acc.draw += mod.power_requirement || mod.power_draw || 0;
      acc.gen += mod.power_output || 0;
      return acc;
    }, { draw: 0, gen: 0 });

    stats.balance = stats.gen - stats.draw;
    return stats;
  },

  getModuleSlots(scheme) {
    if (!scheme) return { total: 0, used: 0, free: 0 };
    const modules = scheme.installed_modules || scheme.modules || [];
    const totalSlots = scheme.total_module_slots || scheme.max_modules || 0;
    const usedSlots = modules.length;

    return {
      total: totalSlots,
      used: usedSlots,
      free: totalSlots - usedSlots
    };
  },

  filterByTechLevel(schemes, minLevel, maxLevel = Infinity) {
    if (!Array.isArray(schemes)) return [];
    return schemes.filter(scheme => {
      const level = scheme.tech_level || 0;
      return level >= minLevel && level <= maxLevel;
    });
  },

  sortByComplexity(schemes, ascending = true) {
    if (!Array.isArray(schemes)) return [];
    return [...schemes].sort((a, b) => {
      const complexityA = (a.tech_level || 0) + (a.module_count || 0) * 0.1;
      const complexityB = (b.tech_level || 0) + (b.module_count || 0) * 0.1;
      return ascending ? complexityA - complexityB : complexityB - complexityA;
    });
  },

  getConstructionRequirements(scheme) {
    if (!scheme) return { materials: [], tools: [], skills: [], time: 0 };

    const baseReq = scheme.construction_requirements || { materials: [], tools: [], skills: [], time: 0 };
    const modules = scheme.installed_modules || scheme.modules || [];

    const modulesReq = modules.reduce((acc, mod) => {
      const modReq = mod.construction_requirements || {};
      acc.materials.push(...(modReq.materials || []));
      acc.tools.push(...(modReq.tools || []));
      acc.skills.push(...(modReq.skills || []));
      acc.time += modReq.time || 0;
      return acc;
    }, { materials: [], tools: [], skills: [], time: 0 });

    return {
      materials: [...new Set([...baseReq.materials, ...modulesReq.materials])],
      tools: [...new Set([...baseReq.tools, ...modulesReq.tools])],
      skills: [...new Set([...baseReq.skills, ...modulesReq.skills])],
      time: (baseReq.time || 0) + modulesReq.time
    };
  },

  getMaintenanceCost(scheme) {
    if (!scheme) return { daily: 0, weekly: 0, monthly: 0 };

    const baseMaintenance = scheme.maintenance_cost || { daily: 0, weekly: 0, monthly: 0 };
    const modules = scheme.installed_modules || scheme.modules || [];

    const modulesMaintenance = modules.reduce((acc, mod) => {
      const modMaint = mod.maintenance_cost || {};
      acc.daily += modMaint.daily || 0;
      acc.weekly += modMaint.weekly || 0;
      acc.monthly += modMaint.monthly || 0;
      return acc;
    }, { daily: 0, weekly: 0, monthly: 0 });

    return {
      daily: (baseMaintenance.daily || 0) + modulesMaintenance.daily,
      weekly: (baseMaintenance.weekly || 0) + modulesMaintenance.weekly,
      monthly: (baseMaintenance.monthly || 0) + modulesMaintenance.monthly
    };
  }
};

// ============================================================================
// ОСНОВНОЙ ЭКСПОРТ
// ============================================================================

const gateway = {
  http: httpClients,

  // 🔥 Итерация 9: публичные алиасы двух HTTP-клиентов (для useSummaryMesh / useSkyboxMesh)
  worldManager: httpClients.world,        // baseURL: world_manager:4002
  universeGateway: httpClients.universe,  // baseURL: universe_gateway:4000

  api: {
    auth: authAPI,
    accessAdmin: accessAdminAPI,
    character: characterAPI,
    activeCharacter: activeCharacterAPI,
    designCharacter: designCharacterAPI,
    npc: npcAPI,
    ontology: ontologyAPI,
    game: gameAPI,
    generation: generationAPI,
    worldManager: worldManagerAPI,
    scheme: schemeAPI,
    spaceStation: spaceStationAPI,
    spaceport: spaceportAPI,
    shipyard: shipyardAPI,
    civilStructure: civilStructureAPI,
    militaryStructure: militaryStructureAPI,
    ship: shipAPI,
    vehicle: vehicleAPI,
    player: {
      async placeCharacterInSpawnPoint(data) {
        const response = await httpClients.universe.post('/api/player/characters/place-in-spawn-point', {
          character_id: data.character_id.toString(),
          spawn_object_id: data.spawn_object_id.toString()
        });
        return response.data;
      },

      async getCharacterPosition(characterId) {
        try {
          const response = await httpClients.universe.get(`/api/player/characters/${characterId}/position`);
          return response.data;
        } catch (error) {
          console.error(`❌ Failed to get character position for ${characterId}:`, error);
          throw error;
        }
      },

      async getCharacterEnvironment(characterId) {
        try {
          const response = await httpClients.universe.get(`/api/player/characters/${characterId}/environment`);
          return response.data;
        } catch (error) {
          console.error(`❌ Failed to get character environment for ${characterId}:`, error);
          throw error;
        }
      },

      async getAvailableActions(characterId) {
        try {
          const response = await httpClients.universe.get(`/api/player/characters/${characterId}/available-actions`);
          return response.data;
        } catch (error) {
          console.error(`❌ Failed to get available actions for ${characterId}:`, error);
          throw error;
        }
      },

      async performAction(characterId, actionType, params = {}) {
        try {
          const response = await httpClients.universe.post(`/api/player/characters/${characterId}/perform-action`, {
            action: actionType,
            params: params
          });
          return response.data;
        } catch (error) {
          console.error(`❌ Failed to perform action for ${characterId}:`, error);
          throw error;
        }
      },

      async getCharacterGoals(characterId) {
        return gameAPI.getCharacterGoals(characterId);
      },

      async getCharacterTasks(characterId) {
        return gameAPI.getCharacterTasks(characterId);
      },

      async sendStimulus(characterId, stimulusData) {
        return gameAPI.sendStimulus(characterId, stimulusData);
      },

      async getCharacterEmotions(characterId) {
        return gameAPI.getCharacterEmotions(characterId);
      }
    },
  },

  constants: {
    CHARACTER_TYPES,
    ALLOWED_CHARACTER_TYPES,
    SCHEME_TYPES
  },

  socket: {
    connectUniverse: (options) => connectSocket('universe_gateway', options),
    connectImcon: (options) => connectSocket('imcon', options),
    connectWorld: (options) => connectSocket('world_manager', options),
    connectSimCharacter: (options) => connectSocket('sim_character', options),
    connectDesigner: (options) => connectSocket('character_designer', options),
    connectGame: (characterId, options = {}) => gameAPI.connectToGame(characterId, options)
  },

  utils: {
    isAuthenticated,
    validateCharacterType,
    getServiceUrls: () => SERVICE_URLS,
    characters: characterUtils,
    ontology: ontologyUtils,
    schemes: schemeUtils,

    formatHealth: (health) => `${health.current}/${health.max}`,

    formatPosition: (position) => {
      if (!position) return 'Неизвестно';
      const location = characterUtils.formatLocation(position);
      const coords = `${position.x?.toFixed(1) || '0.0'}, ${position.y?.toFixed(1) || '0.0'}, ${position.z?.toFixed(1) || '0.0'}`;
      return `${location} (${coords})`;
    },

    calculateTravelTime: (from, to, speed = 1) => {
      const distance = Math.sqrt(
        Math.pow((to.x || 0) - (from.x || 0), 2) +
        Math.pow((to.y || 0) - (from.y || 0), 2) +
        Math.pow((to.z || 0) - (from.z || 0), 2)
      );
      return Math.round(distance / speed * 100) / 100;
    },

    generatePlayerId: () => `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,

    getErrorMessage: (error) => {
      if (!error) return 'Неизвестная ошибка';
      if (typeof error === 'string') return error;
      if (error.message) return error.message;
      if (error.response?.data?.message) return error.response.data.message;
      if (error.response?.data?.error) return error.response.data.error;
      return 'Произошла ошибка при выполнении запроса';
    },

    buildWebSocketUrl,
    extractId,
    validateId,
    isCharacterPlacedInWorld,
    normalizeCharacterPlacement,

    getLocationInfo: characterUtils.getLocationInfoFromPosition,
    formatLocation: characterUtils.formatLocation,

    extractLocationData: (response) => {
      const data = response?.data || response;
      const position = data?.position || data;

      if (position.location_info) {
        return position.location_info;
      }

      return {
        id: position.location || 'unknown',
        name: position.location_name || 'Unknown Location',
        type: position.location_type || 'unknown',
        description: position.location_description || ''
      };
    }
  },
};

export default gateway;
export const {
  http,
  worldManager,      // 🔥 Итерация 9
  universeGateway,   // 🔥 Итерация 9
  api, socket, game, utils, constants
} = gateway;
export { isAuthenticated, validateCharacterType };